import isEqual from 'lodash/isEqual';
import { mapToContactMinDto } from 'pages/contacts/util';
import {
    baseTabWithRates,
    tabFromList,
    toPersonalRateInputs,
    toPricelistWriteDtos,
} from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { changedExceptionPeople, exceptionsOfLists } from 'pages/pricelists/modal/priceExceptions';
import { ClassifierData } from 'types/classifiers';
import { CustomFieldDto } from 'types/customFields';
import {
    EngagementLengthUnit,
    MatterCreateDto,
    MatterDto,
    MatterForm,
    MatterPartyRoleType,
    OfferValueType,
    PersonalRateDraft,
    PricelistTabDraft,
    RemunerationCalcType,
} from 'types/matters';
import { PracticeAreaType } from 'types/practice-areas';
import { MatterPricelist, PricingKind } from 'types/pricelists';
import { buildEntityFieldChangeReasons } from 'utils/changeReasonUtil';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { getClassifierOptionByCode, getOptionsFromClassifier } from 'utils/formUtil';
import { toTimecardIncrementOption } from 'utils/timecardIncrementOptions';
import { payerRowsFromDto, payerRowsToDto, primaryPayerParty } from './matterPayers';
import { dropInactivePersonalRates } from './matterPricingCopy';
import { reduceParties, splitCaseStaff } from './matterUtils';

// The stored lists as the form stages them, a base tab first even on a case that has none yet.
const stagePricelists = (lists: MatterPricelist[]): PricelistTabDraft[] => {
    const tabs = lists.map(tabFromList);
    return tabs.some((tab) => tab.isBase) ? tabs : [baseTabWithRates(), ...tabs];
};

/**
 * The edit form's values for a case, as the case edit page loads them. Dates are kept as the strings the date
 * inputs read.
 */
export const matterDtoToForm = (
    matter: MatterDto,
    {
        classifiers,
        pricelists,
        customFieldValues,
        isAdministratorFieldEnabled,
    }: {
        classifiers: Record<string, ClassifierData>;
        /** The case's stored lists, as `findMatterRates` reads them. */
        pricelists: MatterPricelist[];
        /** `useCustomFields`' `initialCustomFieldsObject` for the case. */
        customFieldValues: Record<string, unknown>;
        isAdministratorFieldEnabled: boolean;
    }
): MatterForm => ({
    readOnlyCaseNumber: matter.caseNumber ?? null,
    name: matter.name,
    clientParty: matter.clientParty ? mapToContactMinDto(matter.clientParty) : null,
    matterStateType:
        (
            getOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.matterStateTypeCode) ?? null,
    matterType: getClassifierOptionByCode<string, string>(classifiers['MATTER_TYPE'], matter.matterTypeCode ?? null),
    reasonOfferLost:
        (
            getOptionsFromClassifier(classifiers['REASON_OFFER_LOST']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.reasonOfferLostCode) ?? null,
    outcomeNotes: matter.outcomeNotes ?? '',
    offerValueType:
        (
            getOptionsFromClassifier(classifiers['OFFER_VALUE_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === (matter.offerValueTypeCode ?? OfferValueType.CASH)) ?? null,
    offerValueLow: matter.offerValueLow ?? '',
    offerValueHigh: matter.offerValueHigh ?? '',
    estimatedEngagementLength: matter.estimatedEngagementLength ?? '',
    estimatedEngagementUnit:
        (
            getOptionsFromClassifier(classifiers['ENGAGEMENT_LENGTH_UNIT']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === (matter.estimatedEngagementUnitCode ?? EngagementLengthUnit.WEEKS)) ?? null,
    originatesFromMatterId: matter.originatesFromMatter?.id ?? null,
    handledByOffice: matter.handledByOffice
        ? {
              ...matter.handledByOffice,
          }
        : null,
    generalNotes: matter.generalNotes ?? '',
    officialReference: matter.officialReference ?? '',
    isUsedTimeUsedForRemuneration: matter.remunerationCalcTypeCode === RemunerationCalcType.EQUALS_USED_TIME,
    isConfidential: matter.isConfidential ?? false,
    confidentialReason: matter.confidentialReason ?? '',
    insiderInfo: matter.insiderInfo ?? '',
    isInsider: matter.isInsider ?? false,
    description: matter.description?.length ? matter.description : [matter.clientParty.name, matter.name].join(' '),
    practiceAreas:
        matter.practiceAreas?.filter((area) => area.practiceAreaTypeCode === PracticeAreaType.PRACTICE_AREA) ?? [],
    secondLevelPracticeAreas:
        matter.practiceAreas
            ?.filter((area) => area.practiceAreaTypeCode === PracticeAreaType.PRACTICE_SUBAREA)
            .map((val) => ({
                ...val,
                name: val.fullPath,
            })) ?? [],
    dmsPracticeArea: matter.dmsPracticeArea
        ? { ...matter.dmsPracticeArea, name: matter.dmsPracticeArea.fullPath }
        : null,
    serviceAgreementContract: matter.serviceAgreementContract || null,
    timecardIncrement: matter.timecardIncrement != null ? toTimecardIncrementOption(matter.timecardIncrement) : null,
    project: matter.project ?? null,
    openedDate: matter.openedDate ? matter.openedDate : null,
    closedDate: matter.closedDate ? matter.closedDate : null,
    responsibleEmployee: matter.responsibleEmployee ?? null,
    supervisedByEmployee: matter.supervisedByEmployee ?? null,
    ...splitCaseStaff(matter.employees ?? [], isAdministratorFieldEnabled),
    primaryContactParty: matter.primaryContactParty
        ? { id: matter.primaryContactParty.id, name: matter.primaryContactParty.name }
        : null,
    representedClients: reduceParties(matter.parties ?? [], MatterPartyRoleType.CLIENT_REPRESENTED),
    isNoOpponent: matter.isNoOpponent ?? false,
    opponentParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OPPONENT),
    targetParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.TARGET),
    otherParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.OTHER_PARTY),
    judgeParties: reduceParties(matter.parties ?? [], MatterPartyRoleType.JUDGE),
    archivedInOffice: matter.archivedInOffice
        ? { id: matter.archivedInOffice.id, name: matter.archivedInOffice.name }
        : null,
    archivedDate: matter.archivedDate ? matter.archivedDate : null,
    archivingNotes: matter.archivingNotes ?? '',
    archivingPropertiesType:
        (
            getOptionsFromClassifier(classifiers['ARCHIVING_PROPERTIES_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.archivingPropertiesTypeCode) ?? null,
    discountPct: matter.discountPct ?? '',
    discountExpiryDate: matter.discountExpiryDate ? formatDateStringInput(new Date(matter.discountExpiryDate)) : null,
    isCostIncluded: matter.isCostIncluded ?? false,
    pricingLimitationType:
        (
            getOptionsFromClassifier(classifiers['PRICING_LIMITATION_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.pricingLimitationTypeCode) ?? null,
    accountingRevenueType:
        (
            getOptionsFromClassifier(classifiers['ACCOUNTING_REVENUE_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.accountingRevenueTypeCode) ?? null,
    capUnitType:
        (
            getOptionsFromClassifier(classifiers['CAP_UNIT_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.capUnitTypeCode) ?? null,
    capLimit: matter.capLimit ?? '',
    fixedFee: matter.fixedFee ?? '',
    recurringFixedFee: matter.recurringFixedFee ?? '',
    billingNotes: matter.billingNotes ?? '',
    invoicingInfo: matter.invoicingInfo ?? '',
    timecardLanguage:
        (
            getOptionsFromClassifier(classifiers['LANGUAGE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.timecardLanguageCode) ?? null,
    primaryRecipientParty: matter.primaryRecipientParty
        ? {
              id: matter.primaryRecipientParty.id,
              name: matter.primaryRecipientParty.name,
              email: matter.primaryRecipientParty.mainEmail || '',
          }
        : null,
    primaryRecipientAdditionalEmails: matter.primaryRecipientAdditionalEmails ?? '',
    secondaryRecipientParties: (matter.parties || [])
        .flatMap(({ matterPartyRoleTypeCode, party }) =>
            matterPartyRoleTypeCode === MatterPartyRoleType.SECONDARY_RECIPIENT ? [party] : []
        )
        .map(({ id, name, mainEmail }) => ({ id, name, email: mainEmail })),
    secondaryRecipientAdditionalEmails: matter?.secondaryRecipientAdditionalEmails ?? '',
    matterClientReference: matter?.matterClientReference ?? '',
    invoiceMatterName: matter?.invoiceMatterName?.length
        ? matter.invoiceMatterName
        : [matter.caseNumber, matter.name].join(' '),
    invoiceMatterNameLocalLanguage: matter.invoiceMatterNameLocalLanguage ?? '',
    invoiceTemplate: matter.invoiceTemplate
        ? { id: matter.invoiceTemplate.id, name: matter.invoiceTemplate.name }
        : null,
    invoicedByOffice: matter.invoicedByOffice ? { ...matter.invoicedByOffice } : null,
    invoicingIntervalType:
        (
            getOptionsFromClassifier(classifiers['INVOICING_INTERVAL_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.invoicingIntervalTypeCode) ?? null,
    payerParty: matter.payerParty,
    payers: payerRowsFromDto(matter),
    invoiceSpecTemplate: matter.invoiceSpecTemplate
        ? { id: matter.invoiceSpecTemplate.id, name: matter.invoiceSpecTemplate.name }
        : null,
    referencedPricelist:
        matter.pricelist || matter.pricingHash
            ? { id: matter.pricelist?.id || null, pricingHash: matter.pricingHash }
            : null,
    pricelists: stagePricelists(pricelists),
    pricingKind: matter.pricingKind ?? PricingKind.ROLE,
    followsPricelist: matter.followsPricelist ?? null,
    pricelistCurrencyType:
        (
            getOptionsFromClassifier(classifiers['CURRENCY']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.pricelistCurrencyTypeCode) ?? null,
    paymentCurrencyType:
        (
            getOptionsFromClassifier(classifiers['CURRENCY']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.paymentCurrencyTypeCode) ?? null,
    isPaymentCurrencyDifferent:
        !!matter.paymentCurrencyTypeCode && matter.paymentCurrencyTypeCode !== matter.pricelistCurrencyTypeCode,
    isJointInvoicingDisabled: matter.isJointInvoiceDisabled ?? false,
    isAutomaticClosingExempt: matter.isAutomaticClosingExempt ?? false,
    matterBlockingPolicyType:
        (
            getOptionsFromClassifier(classifiers['MATTER_BLOCKING_POLICY_TYPE']) as {
                label: string;
                value: string;
            }[]
        ).find((val) => val.value === matter.matterBlockingPolicyTypeCode) ?? null,
    nextInvoicingDate: matter.nextInvoicingDate ?? null,
    paymentNotificationReceivers: matter.paymentNotificationReceivers?.map((item) => item.employee) ?? [],
    dmsLink: matter.dmsLink || null,
    changeReasons: null,
    isAccessibleFirmWide: matter.isAccessibleFirmWide ?? false,
    preserveNonBillableHours: matter.preserveNonBillableHours,
    isExcludedFromXR: matter.isExcludedFromXR,
    isPaperFile: matter.isPaperFile,
    invoiceCustomization: matter.invoiceCustomization,
    isSpecIncludedInInvoice: matter.isSpecIncludedInInvoice ?? false,
    isSpecExcludedFromEmail: matter.isSpecExcludedFromEmail ?? false,
    isAmlBlockingExempt: matter.isAmlBlockingExempt ?? false,
    ...customFieldValues,
});

// Empty means absent, but 0 is a real bound — so this cannot be a truthiness check. The API serialises the amounts as
// JSON numbers while the inputs hold strings, hence the explicit stringify.
const toNullableAmount = (value: string | number | null | undefined): string | null =>
    value === '' || value === null || value === undefined ? null : String(value);

// The Cash/Weeks selections are display defaults, so they are only persisted once the user supplies a figure to qualify.
// Storing them unconditionally would make an untouched case indistinguishable from a deliberate choice.
const mapToMatterDto = (form: MatterForm, customFields: CustomFieldDto[], withPayers: boolean): MatterCreateDto => ({
    name: form.name,
    clientPartyId: form.clientParty?.id ?? null,
    matterStateTypeCode: form.matterStateType?.value ?? null,
    matterTypeCode: form.matterType?.value ?? null,
    reasonOfferLostCode: form.reasonOfferLost?.value ?? null,
    outcomeNotes: form.outcomeNotes || null,
    offerValueTypeCode:
        toNullableAmount(form.offerValueLow) === null && toNullableAmount(form.offerValueHigh) === null
            ? null
            : (form.offerValueType?.value ?? null),
    offerValueLow: toNullableAmount(form.offerValueLow),
    offerValueHigh: toNullableAmount(form.offerValueHigh),
    estimatedEngagementLength: toNullableAmount(form.estimatedEngagementLength),
    estimatedEngagementUnitCode:
        toNullableAmount(form.estimatedEngagementLength) === null
            ? null
            : (form.estimatedEngagementUnit?.value ?? null),
    originatesFromMatterId: form.originatesFromMatterId,
    handledByOfficeId: form.handledByOffice?.id ?? null,
    generalNotes: form.generalNotes,
    officialReference: form.officialReference,
    remunerationCalcTypeCode: form.isUsedTimeUsedForRemuneration
        ? RemunerationCalcType.EQUALS_USED_TIME
        : RemunerationCalcType.EQUALS_BILLED_TIME,
    isConfidential: form.isConfidential,
    // The server reads null as "keep the stored reason", so a cleared reason has to go as "".
    confidentialReason: form.isConfidential ? (form.confidentialReason ?? '') : '',
    description: form.description,
    insiderInfo: form.insiderInfo || null,
    isInsider: form.isInsider ?? false,
    practiceAreas: form.practiceAreas.concat(form.secondLevelPracticeAreas),
    dmsPracticeAreaId: form.dmsPracticeArea?.id || null,
    serviceAgreementContractId: form.serviceAgreementContract?.id ?? null,
    timecardIncrement: form.timecardIncrement?.value ?? null,
    projects: form.project !== null ? [{ id: form.project.id }] : null,
    openedDate: form.openedDate ? form.openedDate : null,
    closedDate: form.closedDate ? form.closedDate : null,
    responsibleEmployeeId: form.responsibleEmployee?.id ?? null,
    supervisedByEmployeeId: form.supervisedByEmployee?.id ?? null,
    primaryContactPartyId: form.primaryContactParty?.id ?? null,
    employees: form.employees,
    administrators: form.administrators,
    representedClients: form.representedClients,
    isNoOpponent: form.isNoOpponent,
    opponentParties: form.opponentParties,
    targetParties: form.targetParties,
    otherParties: form.otherParties,
    judgeParties: form.judgeParties,
    archivedDate: form.archivedDate ? form.archivedDate : null,
    archivedInOfficeId: form.archivedInOffice?.id ?? null,
    archivingNotes: form.archivingNotes,
    archivingPropertiesTypeCode: form.archivingPropertiesType?.value ?? null,
    discountPct: form.discountPct,
    discountExpiryDate: form.discountExpiryDate ? new Date(form.discountExpiryDate) : null,
    isCostIncluded: form.isCostIncluded,
    pricingLimitationTypeCode: form.pricingLimitationType?.value ?? null,
    accountingRevenueTypeCode: form.accountingRevenueType?.value ?? null,
    capUnitTypeCode: form.capUnitType?.value ?? null,
    capLimit: form.capLimit,
    fixedFee: form.fixedFee,
    recurringFixedFee: form.recurringFixedFee,
    billingNotes: form.billingNotes || null,
    invoicingInfo: form.invoicingInfo || null,
    timecardLanguageCode: form.timecardLanguage?.value ?? null,
    primaryRecipientPartyId: form.primaryRecipientParty?.id ?? null,
    primaryRecipientAdditionalEmails: form.primaryRecipientAdditionalEmails ?? null,
    secondaryRecipientParties: form.secondaryRecipientParties.map(({ id }) => ({ id })),
    secondaryRecipientAdditionalEmails: form.secondaryRecipientAdditionalEmails ?? null,
    matterClientReference: form.matterClientReference || null,
    invoiceMatterName: form.invoiceMatterName,
    invoiceMatterNameLocalLanguage: form.invoiceMatterNameLocalLanguage || null,
    invoiceTemplateId: form.invoiceTemplate?.id ?? null,
    invoicedByOfficeId: form.invoicedByOffice?.id ?? null,
    invoicingIntervalTypeCode: form.invoicingIntervalType?.value ?? null,
    // The payer list's primary row is the payer the rest of the app reads, so the legacy field follows it.
    payerPartyId: (withPayers ? primaryPayerParty(form.payers)?.id : form.payerParty?.id) ?? null,
    ...(withPayers ? { payers: payerRowsToDto(form.payers) } : {}),
    invoiceSpecTemplateId: form.invoiceSpecTemplate?.id ?? null,
    referencedPricelist: form.referencedPricelist,
    // The save carries the full set of lists, built in matterFormToRequest; the flat fields of a one-list save stay null.
    rates: null,
    personalRates: null,
    pricelistId: null,
    pricelistCurrencyTypeCode: form.pricelistCurrencyType?.value ?? null,
    paymentCurrencyTypeCode: form.paymentCurrencyType?.value ?? null,
    nextInvoicingDate: form.nextInvoicingDate,
    paymentNotificationReceivers: form.paymentNotificationReceivers,
    // A confidential case is never invoiced jointly, whichever section's save sends it.
    isJointInvoiceDisabled: form.isJointInvoicingDisabled || form.isConfidential,
    isAutomaticClosingExempt: form.isAutomaticClosingExempt,
    matterBlockingPolicyTypeCode: form.matterBlockingPolicyType?.value ?? null,
    isAmlBlockingExempt: form.isAmlBlockingExempt,
    dmsLink: form.dmsLink || null,
    isAccessibleFirmWide: form.isAccessibleFirmWide,
    isExcludedFromXR: form.isExcludedFromXR,
    preserveNonBillableHours: form.preserveNonBillableHours,
    isPaperFile: form.isPaperFile,
    isSpecIncludedInInvoice: form.isSpecIncludedInInvoice,
    isSpecExcludedFromEmail: form.isSpecExcludedFromEmail,
    ...(customFields.length > 0 ? { customFields: customFields } : {}),
    ...(form.invoiceCustomization ? { invoiceCustomization: form.invoiceCustomization } : {}),
    entityFieldChangeReasons: null,
});

/** The create or update body for the case form's values, with what the save must report or refuse. */
export const matterFormToRequest = (
    form: MatterForm,
    {
        customFields,
        initialForm,
        withPricelists = true,
        withPayers = false,
        inactiveEmployeeIds,
        shownPricingKind,
    }: {
        /** `useCustomFields`' `mapCustomFieldsToDto` of the form's values. */
        customFields: CustomFieldDto[];
        /** The loaded form, on an update only. */
        initialForm?: MatterForm;
        /** Off, the update carries `pricelists: null`, which keeps the stored lists as they are. */
        withPricelists?: boolean;
        /** On with the shared payment feature: the save carries the payer list, and its primary row as the payer. */
        withPayers?: boolean;
        /** On a case started from another, the people priced on the source who are no longer active: the create leaves their rates out. */
        inactiveEmployeeIds?: Set<number>;
        /** On a create, the kind the form shows, which the create keeps when the form names none of its own. */
        shownPricingKind?: PricingKind;
    }
): {
    request: MatterCreateDto;
    /** On a case started from another, the people whose personal rates were left out for no longer being active. */
    droppedPeopleNames: string[];
} => {
    const request = mapToMatterDto(form, customFields, withPayers);
    if (!initialForm) {
        const pricingKind = form.pricingKind ?? shownPricingKind;
        if (pricingKind) {
            request.pricingKind = pricingKind;
        }
        if (form.copiedFromMatterId) {
            request.copiedFromMatterId = form.copiedFromMatterId;
        }
        // An employee-priced case prices people only, as the form shows it: the referenced list and role rows that a
        // role-priced service agreement seeded stay behind when the agreement is cleared.
        const perEmployee = pricingKind === PricingKind.EMPLOYEE;
        if (perEmployee) {
            request.referencedPricelist = null;
        }
        // A follower's lists are the followed list's copy, which the server makes: the create carries no prices.
        // Absent, a new employee-priced case without prices follows its service agreement's list.
        if (form.followsPricelist) {
            request.followsPricelist = { id: form.followsPricelist.id };
            request.pricelists = [];
            // What the form shows, none included: absent, the server would take over the source case's exceptions.
            // The followed list's own people come with the server's copy; only an exception of someone no longer
            // active is left out, as the source's personal rates are.
            const exceptions = exceptionsOfLists(form.pricelists);
            const isInactive = (row: PersonalRateDraft) => inactiveEmployeeIds?.has(row.employee.id) ?? false;
            request.pricelistExceptions = toPersonalRateInputs(exceptions.filter((row) => !isInactive(row)));
            return {
                request,
                droppedPeopleNames: exceptions.filter(isInactive).map((row) => row.employee.name),
            };
        }
        if (form.followsPricelist === null) {
            request.followsPricelist = null;
        }
        const fitted = dropInactivePersonalRates(form.pricelists, inactiveEmployeeIds ?? new Set());
        const lists = toPricelistWriteDtos(fitted.pricelists);
        request.pricelists = perEmployee ? lists.map((list) => ({ ...list, rates: [] })) : lists;
        return { request, droppedPeopleNames: fitted.droppedPeopleNames };
    }

    request.entityFieldChangeReasons = buildEntityFieldChangeReasons(form, initialForm, [
        'matterBlockingPolicyType',
        'isAmlBlockingExempt',
    ]);
    if (!withPricelists) {
        request.pricelists = null;
        return { request, droppedPeopleNames: [] };
    }
    // The link goes only when it changed: the server follows or makes the own copy before it writes the lists.
    if ((form.followsPricelist?.id ?? null) !== (initialForm.followsPricelist?.id ?? null)) {
        request.followsPricelist = form.followsPricelist ? { id: form.followsPricelist.id } : null;
    }
    if (form.followsPricelist) {
        request.pricelists = null;
        const exceptions = exceptionsOfLists(form.pricelists);
        if (changedExceptionPeople(exceptionsOfLists(initialForm.pricelists), exceptions).length > 0) {
            request.pricelistExceptions = toPersonalRateInputs(exceptions);
        }
        return { request, droppedPeopleNames: [] };
    }
    const currencyChanged = form.pricelistCurrencyType?.value !== initialForm.pricelistCurrencyType?.value;
    // The save ends the following and the server keeps the followed prices as they are now; the tabs may be older.
    if (form.keepsServerCopy && initialForm.followsPricelist && !currencyChanged) {
        request.pricelists = null;
        return { request, droppedPeopleNames: [] };
    }
    // A currency change wipes the stored lists, so the save must carry the base tab even when empty.
    request.pricelists = toPricelistWriteDtos(form.pricelists, currencyChanged);
    return { request, droppedPeopleNames: [] };
};

/**
 * The form fields each case section edits, by the section's `?edit=` id: the fields its component in
 * `form-sections/` renders, and those its handlers set alongside them. Custom fields are a section of their own.
 * Case details' handlers also set `DETAILS_SPILL_OVER` of Invoice spec, which its save takes once they changed
 * (`matterSectionFields`); the rest of what they set elsewhere, such as the contacts a client change resets, goes as
 * fetched.
 */
export const MATTER_SECTION_FIELDS = {
    details: [
        'readOnlyCaseNumber',
        'name',
        'clientParty',
        'matterStateType',
        'matterType',
        'reasonOfferLost',
        'outcomeNotes',
        'offerValueType',
        'offerValueLow',
        'offerValueHigh',
        'estimatedEngagementLength',
        'estimatedEngagementUnit',
        'handledByOffice',
        'description',
        'generalNotes',
        'officialReference',
        'isUsedTimeUsedForRemuneration',
        'isConfidential',
        'confidentialReason',
        'insiderInfo',
        'isInsider',
        'isAccessibleFirmWide',
        'practiceAreas',
        'secondLevelPracticeAreas',
        'dmsPracticeArea',
        'serviceAgreementContract',
        'timecardIncrement',
        'project',
        'openedDate',
        'closedDate',
        'dmsLink',
    ],
    team: ['supervisedByEmployee', 'responsibleEmployee', 'employees', 'administrators'],
    'client-parties': ['primaryContactParty', 'representedClients'],
    opponents: ['isNoOpponent', 'opponentParties', 'targetParties', 'otherParties', 'judgeParties'],
    pricing: [
        'pricelistCurrencyType',
        'pricelists',
        'referencedPricelist',
        'isPaymentCurrencyDifferent',
        'paymentCurrencyType',
        'pricingLimitationType',
        'capLimit',
        'capUnitType',
        'fixedFee',
        'preserveNonBillableHours',
        'recurringFixedFee',
        'invoicingIntervalType',
        'discountPct',
        'discountExpiryDate',
        'isCostIncluded',
        'billingNotes',
        'invoicingInfo',
    ],
    'invoice-spec': [
        'timecardLanguage',
        'invoicedByOffice',
        'primaryRecipientParty',
        'primaryRecipientAdditionalEmails',
        'secondaryRecipientParties',
        'secondaryRecipientAdditionalEmails',
        'matterClientReference',
        'invoiceMatterName',
        'invoiceMatterNameLocalLanguage',
        'payerParty',
        'payers',
        'invoiceTemplate',
        'invoiceSpecTemplate',
        'invoiceCustomization',
        'isSpecIncludedInInvoice',
        'isSpecExcludedFromEmail',
        'nextInvoicingDate',
        'paymentNotificationReceivers',
        'isJointInvoicingDisabled',
        'matterBlockingPolicyType',
        'isAmlBlockingExempt',
        'changeReasons',
    ],
    accounting: ['accountingRevenueType'],
    archiving: [
        'isAutomaticClosingExempt',
        'isPaperFile',
        'archivedDate',
        'archivedInOffice',
        'archivingNotes',
        'archivingPropertiesType',
    ],
    other: ['isExcludedFromXR'],
    'custom-fields': [],
} satisfies Record<string, (keyof MatterForm)[]>;

export type MatterSectionId = keyof typeof MATTER_SECTION_FIELDS;

/** The invoice name that follows the case name, and the invoicing office that follows the handling office. */
const DETAILS_SPILL_OVER = ['invoiceMatterName', 'invoicedByOffice'] satisfies (keyof MatterForm)[];

/**
 * The section fields for one save, Case details widened to the `DETAILS_SPILL_OVER` fields its handlers changed
 * since it opened. Changed is by value: the submitted values are copies, so even an untouched office is another
 * object.
 */
export const matterSectionFields = (
    edited: MatterForm,
    opened: MatterForm
): Record<MatterSectionId, readonly string[]> => {
    const changed = DETAILS_SPILL_OVER.filter((field) => !isEqual(edited[field], opened[field]));
    return changed.length > 0
        ? { ...MATTER_SECTION_FIELDS, details: [...MATTER_SECTION_FIELDS.details, ...changed] }
        : MATTER_SECTION_FIELDS;
};
